import axios from 'axios';
import { API_URL } from '../config';
import { XIMI_SSO_CALLBACK_PATH } from '../routePaths';
import type { AuthTokens } from '../models/auth';
import {
    getAccessToken,
    getRefreshToken,
    setAccessToken,
    setRefreshToken,
    clearTokens
} from './storage/tokenStorage';
import {
    XIMI_SSO_HEADER,
    XimiSsoUnavailableError,
    ensureFreshXimiSsoAccessToken,
    clearXimiSsoSession
} from './ximi/ximiSsoSession';
import { AuthApiErrorCode } from '../models/auth';
import {
    XIMI_SESSION_HEADER,
    fetchXimiStimulinSessionProof
} from '../stores/ximi/ximiStimulinSession';
import {
    RefreshUnavailableError,
    XIMI_SESSION_LOST_GENERIC_REASON,
    XimiSessionLostError,
    isSessionProofInvalidError,
    isTransientFailure,
    isXimiSsoProofError,
    notifyRateLimited,
    notifyUnavailable
} from './apiFailures';

const api = axios.create({ baseURL: API_URL });

let isRefreshing = false;
let pendingQueue: any[] = [];

const processQueue = (error: unknown, token: string | null) => {
    pendingQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token!);
        }
    });
    pendingQueue = [];
};

/**
 * Session Ximi définitivement morte (SSO invalide, ou appareil évincé) : purge locale + retour
 * login. `reason` alimente le bandeau (`1` = générique, sinon un code métier à message dédié).
 */
export const signOutOnXimiSessionLost = async (
    reason: string = XIMI_SESSION_LOST_GENERIC_REASON
): Promise<void> => {
    await clearTokens();
    await clearXimiSsoSession();
    const { pathname } = window.location;
    if (!pathname.startsWith('/login') && pathname !== XIMI_SSO_CALLBACK_PATH) {
        window.location.href = `/login?ximiError=${reason}`;
    }
};

const handleXimiSsoSessionLost = async (error: unknown, reason?: string): Promise<never> => {
    await signOutOnXimiSessionLost(reason);
    return Promise.reject(error);
};

/**
 * En-têtes du refresh d'un user Ximi : preuve SSO + verdict de session Ozmo. Une session perdue
 * côté Ozmo interrompt le refresh (déconnexion) ; une route indisponible l'empêche (session
 * conservée) — refresher sans jeton serait refusé par StimulinAPI, qui fermerait la session.
 */
const buildRefreshHeaders = async (): Promise<Record<string, string>> => {
    const ssoToken = await ensureFreshXimiSsoAccessToken();
    if (!ssoToken) return {};
    const proof = await fetchXimiStimulinSessionProof();
    if (!proof.ok && proof.lost) throw new XimiSessionLostError(proof.code);
    if (!proof.ok) throw new RefreshUnavailableError(proof.reason);
    return { [XIMI_SSO_HEADER]: ssoToken, [XIMI_SESSION_HEADER]: proof.token };
};

api.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Preuve SSO par requête (users Ximi) — null pour les users non-Ximi (aucune session oidc).
    // AuthProvider injoignable : on part sans preuve, le 401 SSO_* qui suit passera par le
    // renew+retry de l'intercepteur de réponse, qui décidera (session conservée si transitoire).
    const ssoToken = await ensureFreshXimiSsoAccessToken().catch((error: unknown) => {
        if (error instanceof XimiSsoUnavailableError) return null;
        throw error;
    });
    if (ssoToken) {
        config.headers[XIMI_SSO_HEADER] = ssoToken;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Network error or no config (no response from server) — nothing to retry
        if (!originalRequest) {
            return Promise.reject(error);
        }

        // 429 rate-limit API : informer sans toucher à la session, pas de retry automatique
        if (error.response?.status === 429) {
            notifyRateLimited();
            return Promise.reject(error);
        }

        // Preuve SSO rejetée : le token Stimul'In est bon, c'est la session SSO qui cloche.
        // Un renew + retry, sinon session perdue → logout.
        if (isXimiSsoProofError(error)) {
            if (originalRequest._ssoRetry) return handleXimiSsoSessionLost(error);
            originalRequest._ssoRetry = true;

            const ssoToken = await ensureFreshXimiSsoAccessToken().catch((renewError: unknown) => {
                if (!isTransientFailure(renewError)) throw renewError;
                notifyUnavailable();
                // L'échec transitoire, pas le 401 : l'appelant (getUser) doit garder la session.
                return Promise.reject(renewError);
            });
            if (!ssoToken) return handleXimiSsoSessionLost(error);
            originalRequest.headers[XIMI_SSO_HEADER] = ssoToken;
            return api(originalRequest);
        }

        if (error.response?.status !== 401 || originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingQueue.push({ resolve, reject });
            }).then((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
            isRefreshing = false;
            processQueue(error, null);
            return Promise.reject(error);
        }

        try {
            // La rotation d'un user Ximi exige la preuve SSO et le verdict de session Ozmo.
            const headers = await buildRefreshHeaders();
            const { data } = await axios.post<AuthTokens>(
                `${API_URL}/auth/refresh`,
                { refreshToken },
                { headers }
            );
            await setAccessToken(data.accessToken);
            await setRefreshToken(data.refreshToken);
            processQueue(null, data.accessToken);
            originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
            return await api(originalRequest);
        } catch (refreshError) {
            processQueue(refreshError, null);
            if (refreshError instanceof XimiSessionLostError) {
                return await handleXimiSsoSessionLost(refreshError, refreshError.code);
            }
            if (isSessionProofInvalidError(refreshError)) {
                return await handleXimiSsoSessionLost(
                    refreshError,
                    AuthApiErrorCode.SESSION_PROOF_INVALID
                );
            }
            if (isXimiSsoProofError(refreshError)) {
                return await handleXimiSsoSessionLost(refreshError);
            }
            if (isTransientFailure(refreshError)) {
                notifyUnavailable();
                return await Promise.reject(refreshError);
            }
            await clearTokens();
            // Avoid redirect loop if already on login page, and don't interrupt
            // the Ximi SSO callback (it has its own auth flow in progress).
            const { pathname } = window.location;
            if (!pathname.startsWith('/login') && pathname !== XIMI_SSO_CALLBACK_PATH) {
                window.location.href = '/login';
            }
            return await Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default api;
